import React, {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { fabric } from 'fabric';
import { MapInteractionCSS } from 'react-map-interaction';
import { FabricContext } from './FabricContext';

interface Props {
  jsonData?: any;
  width?: any;
  height?: any;
  state?: any;
  dispatch?: any;
}

export const PreRegFloorPlan = (props: Props) => {
  const {
    jsonData = null,
    width = 816,
    height = 800,
    state,
    dispatch,
  } = props;

  const canvasEl = useRef(null);

  const {
    canvas,
    initCanvas,
    activeObject,
    setActiveObject,
    loadFromJSON,
  }: any = useContext(FabricContext);

  const [value, setValue] = useState({ scale: 1, translation: { x: 0, y: 0 } });
  const [cWidth, setCWidth] = useState(0);
  const [cHeight, setCHeight] = useState(0);

  const drawCondition = () => {
    return (
      (activeObject !== null &&
        state?.selectedRoom?.filter(x => x?.IsSelected === true)?.length !== 1 &&
        activeObject?.data?.FloorPlan?.filter(
          x => x?.IsPreCheckin === true && x?.IsHotel === true
        )[0]?.Coordinates !== '0,0,101,0,101,101,0,101') ||
      (activeObject !== null &&
        state?.selectedRoom?.filter(x => x?.IsSelected === true)?.length === 1 &&
        activeObject?.data?.IsSelected === true &&
        activeObject?.data?.FloorPlan?.filter(
          x => x?.IsPreCheckin === true && x?.IsHotel === true
        )[0]?.Coordinates !== '0,0,101,0,101,101,0,101')
    );
  };

  const DistanceFormula = (x1, x2, y1, y2) => {
    return Math.sqrt(Math.pow(x2 - x1, 2) + Math.pow(y2 - y1, 2));
  };

  const Draw = (room) => {
    let isUserDraw: boolean = false;
    let polygonCoordinate: any[] = [];
    let circle: any;
    let text: any;
    let group: any;

    const fillColor = (isSelected: boolean) =>
      isSelected
        ? '#00bf0f'
        : state?.selectedRoom?.filter(x => x?.IsSelected === true)?.length === 1
        ? '#dcdcdc'
        : '#ff9800';

    if (
      room &&
      room?.FloorPlan?.filter(
        x => x?.IsPreCheckin === true && x?.IsHotel === false
      )[0]?.Coordinates
    ) {
      const splitCoord = room.FloorPlan
        .filter(x => x?.IsPreCheckin === true && x?.IsHotel === false)[0]
        ?.Coordinates?.split(',');

      if (splitCoord?.length === 8) {
        const [tl_x, tl_y, tr_x, tr_y, br_x, br_y, bl_x, bl_y] = splitCoord;
        const topLine = DistanceFormula(tr_x, tl_x, tr_y, tl_y);
        const rightLine = DistanceFormula(br_x, tr_x, br_y, tr_y);
        const bottomLine = DistanceFormula(bl_x, br_x, bl_y, br_y);
        const leftLine = DistanceFormula(tl_x, bl_x, tl_y, bl_y);

        isUserDraw = !(
          (topLine === rightLine && topLine === bottomLine && topLine === leftLine) ||
          (topLine === bottomLine && rightLine === leftLine)
        );
      } else {
        isUserDraw = true;
      }

      for (var a = 0; a < splitCoord?.length; a += 2) {
        if (a !== splitCoord?.length - 1) {
          polygonCoordinate.push(
            new fabric.Point(parseFloat(splitCoord[a]), parseFloat(splitCoord[a + 1]))
          );

          circle = new fabric.Circle({
            radius: 50,
            fill: fillColor(room?.IsSelected),
            top: polygonCoordinate[0]?.y,
            left: polygonCoordinate[0]?.x,
          });
        }
      }
    } else {
      circle = new fabric.Circle({
        radius: 50,
        fill: fillColor(room?.IsSelected),
        top: polygonCoordinate[0]?.y,
        left: polygonCoordinate[0]?.x,
      });
    }

    const cloneGroup = new fabric.Group([circle], {});

    text = new fabric.Text(room?.RoomNo, {
      fontFamily: 'Poppins',
      fontSize: 12,
      lineHeight: 14,
      fontWeight: 700,
      fill: room?.IsSelected === false ? 'black' : 'white',
      textAlign: 'center',
      top: cloneGroup?.aCoords?.tl?.y + 40,
      left: cloneGroup?.aCoords?.tl?.x + 40,
    });

    group = new fabric.Group([text, cloneGroup], {
      transparentCorners: false,
      hasBorders: false,
      hasRotatingPoint: true,
      lockScalingFlip: state?.selectedFloor?.ImageURL !== undefined,
      newCoord: polygonCoordinate,
      isUserDraw,
      hasControls: false,
      lockMovementX: state?.selectedFloor?.ImageURL !== undefined ? false : true,
      lockMovementY: state?.selectedFloor?.ImageURL !== undefined ? false : true,
      data: room,
      objectCaching: false,
    });

    text.bringToFront();
    canvas?.add(group).renderAll();
  };

  const selected = () => {
    const newArr: any = state?.selectedRoom;
    const sel = activeObject?.data;
    sel.IsSelected = !sel.IsSelected;
    dispatch({ type: 'selectedRoom', payload: newArr });
    dispatch({
      type: 'choosenRoom',
      payload: sel.IsSelected === false ? '' : activeObject?.data,
    });
  };

  const updateActiveObject = useCallback(
    e => {
      if (!e) return;
      setActiveObject(canvas.getActiveObject());
      canvas.renderAll();
    },
    [canvas, setActiveObject]
  );

  useLayoutEffect(() => {
    if (jsonData) {
      loadFromJSON(canvasEl.current, jsonData);
    } else {
      initCanvas(canvasEl.current, { width, height });
    }
  }, [canvasEl, initCanvas, loadFromJSON, jsonData, height, width]);

  useEffect(() => {
    if (!canvas) return;
    canvas.on('selection:created', updateActiveObject);
    canvas.on('selection:updated', updateActiveObject);
    canvas.on('selection:cleared', updateActiveObject);
    return () => {
      canvas.off('selection:created');
      canvas.off('selection:cleared');
      canvas.off('selection:updated');
    };
  }, [canvas, updateActiveObject]);

  useEffect(() => {
    const img = new Image();
    img.src = state?.selectedFloor?.ImageURL;
    img.onload = () => {
      setCHeight(img.naturalHeight);
      setCWidth(img.naturalWidth);
    };
  }, [state?.selectedFloor?.ImageURL]);

  useEffect(() => {
    canvas?.clear();
    canvas?.setDimensions({ width: cWidth, height: cHeight });
    fabric.Image.fromURL(state?.selectedFloor?.ImageURL, img => {
      canvas?.setBackgroundImage(img, canvas.renderAll.bind(canvas));
    });
    state?.selectedFloor?.Room?.forEach(el => {
      if (el?.ID) Draw(el);
    });
  }, [state?.selectedFloor?.Room, state?.selectedFloor?.ImageURL, cWidth, cHeight]);

  useEffect(() => {
    if (drawCondition()) {
      selected();
      canvas?.remove(...canvas?.getObjects());
      state?.selectedRoom?.forEach(el => Draw(el));
    }
  }, [
    activeObject,
    state?.selectedFloor?.ImageURL,
    state?.selectedRoom?.filter(x => x?.IsSelected === true)?.length,
  ]);

  return (
    <MapInteractionCSS value={value} onChange={v => setValue(v)} maxScale={5}>
      <canvas ref={canvasEl} id="fabric-canvas" />
    </MapInteractionCSS>
  );
};
